import { v4 as uuidV4 } from 'uuid';

import { getErrorMessage } from '~/helpers/utils/getErrorMessage';
import useUiNotification from '~/composables/useUiNotification';
import { useCart } from '~/composables/custom/useCart/useCart';
import { useBFF } from '~/composables/useBFF/useBFF';
import { useUsercentrics } from '~/composables/custom/useUsercentrics/useUsercentrics';
import type { Address, Customer, Maybe } from '~/types/GraphQL';

const user = ref<Maybe<Customer> | undefined>();
const loading = ref(false);
const updating = ref(false);

export const useUser = () => {
  const { setCart } = useCart();
  const { isPersistBasketConsentGiven } = useUsercentrics();

  const { $me, $customers } = useBFF();
  const { $domain } = useNuxtApp();
  const { load: loadWishlist, clearWishlist, addToWishlistAfterAuth } = useWishlist();
  const router = useRouter();

  const initialError = {
    deleteAddress: undefined,
    updateAddress: undefined,
    saveAddress: undefined,
    saveBirthDate: undefined,
    registerUser: undefined,
    login: undefined,
    updateUser: undefined,
    deleteUser: undefined,
    save: undefined,
    load: undefined,
  };

  const error = ref<Record<keyof typeof initialError, string | undefined>>(initialError);
  const { send } = useUiNotification();

  const isAuthenticated = computed(() => !!user.value);

  const {
    isLoginDrawerOpen,
    toggleLoginDrawer,
  } = useUiState();

  interface LoginParams {
    username: string;
    password: string;
  }

  const userLogin = async (form: LoginParams) => {
    const { username, password } = form;
    loading.value = true;
    error.value.login = undefined;
    try {
      const { data } = await $me.signIn({ email: username, password, rememberMe: isPersistBasketConsentGiven() });

      const { cart, customer } = data?.user || {};
      setUser(customer);
      await setCart(cart);
      await addToWishlistAfterAuth();

      if (isLoginDrawerOpen.value) {
        toggleLoginDrawer();
        if (router.currentRoute.value.query.redirectTo) {
          navigateTo(router.currentRoute.value.query.redirectTo as string);
        }
      }
    }
    catch {
      error.value.login = 'Login error';
    }
    finally {
      loading.value = false;
    }
  };

  /**
   * @deprecated Use userLogin instead
   *
   * @param userForm
   */
  const login = async (userForm: LoginParams) => {
    await userLogin({ username: userForm.username, password: userForm.password });
  };

  // SSO
  const signIn = async (redirectTo: string) => {
    try {
      await $customers.authenticate(redirectTo, isPersistBasketConsentGiven());
    }
    catch {
      send({
        message: 'somethingWentWrong',
        type: 'danger',
      });
      navigateTo('/');
    }
  };

  const getUserAndCart = async () => {
    try {
      const { data } = await $me.getMe();

      await setCart(data?.activeCart);
      setUser(data?.customer);

      return data;
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.load = err?.message;
      }
    }
  };

  const load = async () => {
    const data = await getUserAndCart();

    if (data?.customer) {
      await loadWishlist();
    }
    return data?.customer;
  };

  const isUserLoggedIn = async () => {
    let isLoggedIn;
    if (isAuthenticated.value) {
      isLoggedIn = isAuthenticated.value;
    }
    else {
      const { data } = await $me.isLoggedIn();
      isLoggedIn = data;
    }
    return isLoggedIn;
  };

  const deleteAddress = async ({ addressId }: { addressId: string }) => {
    if (!isAuthenticated.value || !addressId) {
      return;
    }
    try {
      updating.value = true;
      const { data } = await $me.removeAddress(addressId, { version: user.value?.version });

      if (data && user.value) {
        setUser({
          ...user.value,
          ...data,
          addresses: user.value?.addresses.filter(({ id }) => id !== addressId),
        });

        send({
          icon: 'profile_filled',
          message: 'Address successfully removed.',
          type: 'success',
        });
      }
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.deleteAddress = err?.message;
      }
      send({
        icon: 'profile_filled',
        message: 'Sorry! It was not possible to delete your address. Please try again later.',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const setUser = (newUser: Maybe<Customer> | undefined) => {
    user.value = newUser;

    return user.value;
  };

  const getAddress = (address: Address) => ({
    ...(address || {}),
    key: address.key || uuidV4(),
    country: address.country || $domain.countryCode,
    email: address.email || user.value?.email,
  });

  const updateAddress = async ({ address, isDefault, type }: { address: Address; type?: 'shipping' | 'billing'; isDefault: boolean }) => {
    if (!isAuthenticated.value || !address) {
      return undefined;
    }

    try {
      updating.value = true;

      const { data } = await $me.updateAddress(address.id as string, {
        version: user.value?.version, address: getAddress(address), isDefault, type,
      });

      if (data) {
        setUser({ ...user.value, ...data });

        send({
          icon: 'profile_filled',
          message: 'Address successfully updated.',
          type: 'info',
        });
      }
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.updateAddress = err?.message;
      }
      send({
        icon: 'profile_filled',
        message: 'Sorry! It was not possible to update your address. Please try again later.',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
    return address;
  };

  const saveAddress = async ({ address, type, isDefault }: { address: Address; type?: 'shipping' | 'billing'; isDefault?: boolean }) => {
    if (!isAuthenticated.value || !address) {
      return undefined;
    }

    try {
      updating.value = true;

      const { data } = await $me.addAddress({ version: user.value?.version, type, address: getAddress(address), isDefault });
      if (!data) {
        throw new Error('Error adding addresses');
      }

      setUser({ ...user.value, ...data });

      send({
        icon: 'profile_filled',
        message: 'Address successfully added.',
        type: 'success',
      });

      return data;
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.saveAddress = err?.message;
      }
      send({
        icon: 'profile_filled',
        message: 'Sorry! It was not possible to save your new address in your profile.',
        type: 'warning',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const updateBirthDate = async ({ dateOfBirth }: { dateOfBirth: string }) => {
    error.value.saveBirthDate = undefined;
    updating.value = true;
    try {
      const { data } = await $me.updateBirthDate({
        version: user.value?.version,
        dateOfBirth,
      });

      if (data) {
        setUser({
          ...user.value,
          ...data,
        });
      }
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.saveBirthDate = err?.message;
      }
    }
    finally {
      updating.value = false;
    }
  };

  const getRegistrationPayload = (form: any) => {
    const userBillingAddress = {
      id: undefined,
      key: form.key ?? uuidV4(),
      salutation: form.salutation,
      firstName: form.firstName,
      lastName: form.lastName,
      streetName: form.streetName,
      streetNumber: form.streetNumber,
      additionalStreetInfo: form.additionalStreetInfo,
      email: form.email ?? user.value?.email,
      city: form.city,
      postalCode: form.postalCode,
      country: form.country,
      phone: form.phone,
      mobile: form.mobile,
      companyName: form.companyName,
      vatId: form.vatId,
    };

    return {
      salutation: form.salutation,
      title: form.title,
      firstName: form.firstName,
      lastName: form.lastName,
      dateOfBirth: form.dateOfBirth,
      email: form.email,
      password: form.password,
      addresses: [userBillingAddress],
      billingAddresses: [0],
    };
  };

  /**
   * Sign up user vie middleware
   *
   * @returns Newly created customer
   */
  const registerUser = async (form: any) => {
    const registrationForm = getRegistrationPayload(form);
    updating.value = true;
    error.value.login = undefined;
    error.value.registerUser = undefined;
    try {
      const { data } = await $customers.register({ registrationForm });

      if (!data?.customerNumber) {
        return data;
      }

      // Login the new registered user
      const loginUser = {
        username: registrationForm.email,
        password: registrationForm.password,
      };

      await userLogin(loginUser);
    }
    catch (err: any) {
      error.value.registerUser = err?.message ?? 'An error occured';
      send({
        icon: 'profile',
        message: getErrorMessage(err?.message),
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const updateUser = async ({
    dateOfBirth, salutation, title, lastName,
  }: { dateOfBirth: string; salutation: string; title: string; lastName: string }) => {
    try {
      error.value.updateUser = undefined;

      if (!isAuthenticated.value || !lastName || ($domain.dateOfBirthRequired && !dateOfBirth)) {
        return Promise.reject();
      }
      updating.value = true;
      const { data } = await $me.updateProfile({
        version: user.value?.version,
        dateOfBirth,
        salutation,
        title,
        lastName,
      });

      send({
        icon: 'profile_filled',
        message: 'The user profile has been successfully updated!',
        type: 'success',
      });

      if (data) {
        setUser({ ...user.value, ...data });
      }

      return data;
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.updateUser = err?.message;
      }

      send({
        icon: 'profile_filled',
        message: 'The user profile has not been updated. Please try again',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const deleteProfile = async () => {
    try {
      if (!isAuthenticated.value) {
        return;
      }
      error.value.deleteUser = undefined;

      const { data }: any = await $customers.deleteUserProfile();

      if (import.meta.client && data?.redirectUrl) {
        window.location.href = data.redirectUrl;
      }
      await clearUser();
    }
    catch {
      send({
        icon: 'profile_filled',
        message: 'delete account error',
        type: 'danger',
      });
    }
  };

  const clearUser = async () => {
    await setCart(null, false);
    await setUser(null);
    await clearWishlist();
  };

  const logout = async () => {
    await $customers.logout();
    await clearUser();
  };

  const changePassword = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    if (!isAuthenticated.value) {
      return;
    }

    error.value.save = undefined;
    updating.value = true;

    try {
      await $me.changePassword({ currentPassword, newPassword, version: user.value?.version });

      send({
        icon: 'profile_filled',
        message: 'Password Changed',
        type: 'success',
      });

      const userEmail = user.value?.email;

      await logout();

      await userLogin({ username: userEmail as string, password: newPassword });
      await navigateTo('/');
    }
    catch (err) {
      if (err instanceof Error) {
        error.value.save = err.message;
      }

      send({
        icon: 'profile_filled',
        message: 'Password could not be changed. Please try again later.',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  return {
    load,
    loading,
    user,
    setUser,
    deleteProfile,
    deleteAddress,
    updateAddress,
    error: computed(() => error.value),
    updating: computed(() => updating.value),
    saveAddress,
    isAuthenticated,
    updateBirthDate,
    logout,
    clearUser,
    registerUser,
    updateUser,
    changePassword,
    isUserLoggedIn,
    getUserAndCart,

    login,
    userLogin,
    signIn,
  };
};
